import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { SelectIdea, SelectComment, ideaStatusSchema } from "@db/schema";
import { MessageSquare, Pencil } from "lucide-react";
import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/auth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const commentSchema = z.object({
  content: z.string().min(1).max(500),
});

type IdeaDialogProps = {
  idea: SelectIdea | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showAdminControls?: boolean;
};

type IdeaWithComments = SelectIdea & {
  comments: (SelectComment & { username: string })[];
};

const statusColors = {
  'Needs Review': 'bg-red-500 text-white',
  'Under Consideration': 'bg-[#5a8ba7] text-white',
  'In Progress': 'bg-[#799b3e] text-white',
  'Rejected': 'bg-[#4b4f54]/50 text-white',
  'Complete': 'bg-[#4b4f54] text-white',
} as const;

const statusTextColors = {
  'Needs Review': 'text-red-500',
  'Under Consideration': 'text-[#5a8ba7]',
  'In Progress': 'text-[#799b3e]',
  'Rejected': 'text-[#4b4f54]/50',
  'Complete': 'text-[#4b4f54]',
} as const;

type Status = keyof typeof statusColors;

export function IdeaDialog({ idea: initialIdea, open, onOpenChange, showAdminControls = false }: IdeaDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [deleteCommentId, setDeleteCommentId] = useState<number | null>(null);
  const [idea, setIdea] = useState<SelectIdea | null>(initialIdea);

  useEffect(() => {
    setIdea(initialIdea);
  }, [initialIdea]);

  const form = useForm<z.infer<typeof commentSchema>>({
    resolver: zodResolver(commentSchema),
    defaultValues: {
      content: "",
    },
  });

  const editForm = useForm<z.infer<typeof commentSchema>>({
    resolver: zodResolver(commentSchema),
    defaultValues: {
      content: "",
    },
  });

  const { data: ideaWithComments, isLoading } = useQuery<IdeaWithComments>({
    queryKey: ["/api/ideas", idea?.id],
    queryFn: async () => {
      if (!idea) return null;
      const res = await fetch(`/api/ideas/${idea.id}`);
      if (!res.ok) throw new Error("Failed to fetch idea details");
      return res.json();
    },
    enabled: !!idea,
  });

  const addCommentMutation = useMutation({
    mutationFn: async (values: z.infer<typeof commentSchema>) => {
      if (!idea) return;
      const res = await fetch(`/api/ideas/${idea.id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(error);
      }
      return res.json();
    },
    onSuccess: () => {
      form.reset();
      queryClient.invalidateQueries({ queryKey: ["/api/ideas", idea?.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/ideas"] });
      toast({
        title: "Success",
        description: "Comment added successfully",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add comment",
      });
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: async ({ id, content }: { id: number; content: string }) => {
      const res = await fetch(`/api/comments/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(error);
      }
      return res.json();
    },
    onSuccess: () => {
      editForm.reset();
      setEditingCommentId(null);
      queryClient.invalidateQueries({ queryKey: ["/api/ideas", idea?.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/ideas"] });
      toast({
        title: "Success",
        description: "Comment updated successfully",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update comment",
      });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: number) => {
      const res = await fetch(`/api/comments/${commentId}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(error);
      }
      return res.json();
    },
    onSuccess: () => {
      setEditingCommentId(null);
      queryClient.invalidateQueries({ queryKey: ["/api/ideas", idea?.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/ideas"] });
      toast({
        title: "Success",
        description: "Comment deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete comment",
      });
    },
  });

  const statusMutation = useMutation({
    mutationFn: async (status: Status) => {
      const res = await fetch(`/api/admin/ideas/${idea?.id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
        credentials: "include"
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(error);
      }
      return res.json();
    },
    onSuccess: (updatedIdea) => {
      setIdea(prev => prev ? { ...prev, status: updatedIdea.status, isApproved: updatedIdea.isApproved } : null);

      queryClient.invalidateQueries({ queryKey: ["/api/ideas", idea?.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ideas"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ideas"] });
      toast({
        title: "Success",
        description: "Status updated successfully",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update status",
      });
    },
  });

  const startEditing = (comment: any) => {
    // Only allow editing if the user has permission
    if (canEditComment(comment)) {
      editForm.reset({ content: comment.content });
      setEditingCommentId(comment.id);
    }
  };

  // Function to handle delete button click
  const handleDeleteClick = (commentId: number) => {
    setDeleteCommentId(commentId);
  };

  // Function to confirm deletion
  const confirmDelete = () => {
    if (deleteCommentId !== null) {
      deleteCommentMutation.mutate(deleteCommentId);
      setDeleteCommentId(null);
    }
  };

  // Function to cancel deletion
  const cancelDelete = () => {
    setDeleteCommentId(null);
  };

  // Check if the current user can edit a comment
  const canEditComment = (comment: any) => {
    // Allow if user is an admin or the comment author
    return user?.isAdmin || user?.id === comment.userId;
  };

  const formatCommentDate = (dateInput: string | Date) => {
    // Parse the server timestamp directly (in UTC format, e.g. "2025-03-20T16:06:28.598Z")
    let date;
    
    if (typeof dateInput === 'string') {
      if (dateInput.endsWith('Z')) {
        // Extract time directly from the timestamp without timezone conversion
        const matches = dateInput.match(/T(\d{2}):(\d{2})/);
        if (matches) {
          // Get hours and minutes from the UTC time
          const hours = parseInt(matches[1], 10);
          const minutes = matches[2];
          
          // Format in 12-hour format
          const period = hours >= 12 ? 'PM' : 'AM';
          const displayHours = hours % 12 || 12;
          
          // Check if it's today
          const today = new Date();
          const timestampDate = new Date(dateInput);
          const isToday = 
            today.getUTCFullYear() === timestampDate.getUTCFullYear() &&
            today.getUTCMonth() === timestampDate.getUTCMonth() &&
            today.getUTCDate() === timestampDate.getUTCDate();
            
          if (isToday) {
            // For today's comments, show time in 12-hour format
            return `${displayHours}:${minutes} ${period}`;
          } else {
            // For older comments, show the date
            const dateObj = new Date(dateInput);
            const month = dateObj.toLocaleString('en-US', { month: 'short' });
            const day = dateObj.getUTCDate();
            const year = dateObj.getUTCFullYear();
            return `${month} ${day}, ${year}`;
          }
        }
      }
      
      // Fallback to standard processing
      date = new Date(dateInput);
    } else {
      date = dateInput;
    }
    
    // Fallback formatting (should rarely be used)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const commentDate = new Date(date);
    commentDate.setHours(0, 0, 0, 0);
    
    const isToday = commentDate.getTime() === today.getTime();
    
    if (isToday) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  if (!idea) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{idea.title}</DialogTitle>
            <DialogDescription>View and discuss this idea's details</DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            {showAdminControls && (
              <div className="mb-4">
                <Select
                  value={idea.status}
                  onValueChange={(value: Status) => statusMutation.mutate(value)}
                >
                  <SelectTrigger className={`w-[200px] ${statusColors[idea.status as Status]} rounded-md`}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {Object.keys(statusColors).map((status) => (
                      <SelectItem
                        key={status}
                        value={status}
                        className={`${statusTextColors[status as Status]} hover:bg-gray-100`}
                      >
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="w-full">
              <p style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                hyphens: 'auto',
                maxWidth: '100%'
              }} className="text-gray-600">
                {idea.description}
              </p>
            </div>

            <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
              <span>Posted on {new Date(idea.createdAt).toLocaleDateString()}</span>
              <span>•</span>
              <span className="text-purple-600 font-medium">By {idea.username || 'Anonymous'}</span>
              <span>•</span>
              <span>{idea.upvotes} upvotes</span>
              <span>•</span>
              <span>{idea.downvotes} downvotes</span>
            </div>
          </div>

          <Separator className="my-6" />

          <div>
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="h-5 w-5" />
              <h3 className="font-semibold">Comments</h3>
              <span className="text-sm text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                {ideaWithComments?.comments?.length || 0}
              </span>
            </div>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit((data) => addCommentMutation.mutate(data))}
                className="space-y-4 mb-6"
              >
                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          placeholder="Add a comment..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={addCommentMutation.isPending}
                  >
                    {addCommentMutation.isPending ? "Adding..." : "Add Comment"}
                  </Button>
                </div>
              </form>
            </Form>

            {isLoading ? (
              <div className="space-y-4">
                {[1, 2].map((i) => (
                  <div key={i} className="h-20 bg-gray-100 rounded-md animate-pulse" />
                ))}
              </div>
            ) : ideaWithComments?.comments?.length ? (
              <div className="space-y-4">
                {ideaWithComments.comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="p-4 rounded-lg bg-gray-50"
                  >
                    {editingCommentId === comment.id ? (
                      <Form {...editForm}>
                        <form
                          onSubmit={editForm.handleSubmit((data) =>
                            updateCommentMutation.mutate({ id: comment.id, content: data.content })
                          )}
                          className="space-y-4"
                        >
                          <FormField
                            control={editForm.control}
                            name="content"
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Textarea {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <div className="flex justify-end gap-2">
                            <Button
                              type="submit"
                              disabled={updateCommentMutation.isPending}
                            >
                              {updateCommentMutation.isPending ? "Saving..." : "Save"}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setEditingCommentId(null)}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              variant="destructive"
                              onClick={() => handleDeleteClick(comment.id)}
                              disabled={deleteCommentMutation.isPending}
                            >
                              {deleteCommentMutation.isPending ? "Deleting..." : "Delete"}
                            </Button>
                          </div>
                        </form>
                      </Form>
                    ) : (
                      <>
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1">
                            <p className="text-gray-600">{comment.content}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-sm font-medium text-purple-600">
                                {comment.username}
                              </span>
                              <span className="text-sm text-gray-500">•</span>
                              <span className="text-sm text-gray-500">
                                {formatCommentDate(comment.createdAt)}
                              </span>
                            </div>
                          </div>
                          {canEditComment(comment) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => startEditing(comment)}
                              className="h-8 w-8"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500">No comments yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteCommentId !== null} onOpenChange={open => !open && setDeleteCommentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this comment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
